(() => {
  const waitForSpark = async () => {
    const ready = window.grsClearanceReady;
    if (!ready) return null;
    const spark = await ready;
    return typeof spark?.getPricingForVariant === 'function' && await spark.isLoggedIn() ? spark : null;
  };

  const text = (tag, value, className) => {
    const node = document.createElement(tag);
    node.textContent = value;
    if (className) node.className = className;
    return node;
  };
  const eligible = (variant, pricing) => {
    const currency = String(pricing?.currencyCode || '').toUpperCase();
    const key = {GBP: 'gbp', EUR: 'eur'}[currency];
    const raw = key ? variant[key] : null;
    if (raw === null || raw === undefined || raw === '' || pricing?.price === null || pricing?.price === undefined) return null;
    const before = Number(raw);
    const now = Number(pricing.price);
    if (!Number.isFinite(before) || !Number.isFinite(now) || before <= 0 || now < 0 || Math.round(before * 100) <= Math.round(now * 100)) return null;
    return {variant, before, now, currency};
  };
  window.grsClearanceEligibility = eligible;
  class TradeClearance extends HTMLElement {
    async connectedCallback() {
      this.hidden = true;
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      try {
        const data = JSON.parse(this.querySelector('[data-clearance-variants]')?.textContent || '[]');
        if (this.dataset.demo === 'true') {
          this.render(data.map(v => eligible(v, {price: v.demoPrice, currencyCode: this.dataset.currency})).filter(Boolean), data.length);
          return;
        }
        if (!data.some(v => Number(v.gbp) > 0 || Number(v.eur) > 0)) return;
        const spark = await waitForSpark();
        if (!spark || !(await spark.isLoggedIn()) || !this.isConnected || generation !== this.generation) return;
        const results = await Promise.allSettled(data.map(async variant => {
          if (!variant.sku || !(Number(variant.gbp) > 0 || Number(variant.eur) > 0)) return null;
          return eligible(variant, await spark.getPricingForVariant(this.dataset.productId, variant.sku));
        }));
        if (!this.isConnected || generation !== this.generation) return;
        const rows = results.flatMap(result => result.status === 'fulfilled' && result.value ? [result.value] : []);
        this.render(rows, data.length);
      } catch { this.hidden = true; }
    }
    disconnectedCallback() { this.generation = (this.generation || 0) + 1; }
    render(rows, count) {
      const output = this.querySelector('[data-clearance-output]');
      if (!output) return;
      output.replaceChildren();
      this.hidden = true;
      if (!rows.length) return;
      const labels = window.grsClearanceLabels || {};
      const money = row => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency', currency:row.currency});
      const sorted = [...rows].sort((a,b) => a.now - b.now);
      const first = sorted[0];
      const uniform = rows.every(r => r.currency === first.currency && r.now === first.now && r.before === first.before);
      const complete = rows.length === count;
      const line = document.createElement('div'); line.className = 'grs-clearance-line';
      line.append(text('span', labels.badge || 'Clearance', 'grs-clearance-badge'));
      const varying = !uniform || !complete;
      if (varying) line.append(text('span', labels.from || 'From'));
      const old = text('s', money(first).format(first.before), 'grs-clearance-before');
      old.setAttribute('aria-label', `${labels.before || 'Original trade price'} ${money(first).format(first.before)}`);
      const current = text('strong', money(first).format(first.now), 'grs-clearance-now');
      current.setAttribute('aria-label', `${labels.now || 'Clearance price'} ${money(first).format(first.now)}`);
      line.append(old);
      if (this.dataset.mode !== 'pdp') line.append(current);
      if (!complete) line.append(text('span', labels.selected || 'Selected variants only', 'grs-clearance-scope'));
      output.append(line);
      if (this.dataset.mode === 'pdp' && varying) {
        const table = document.createElement('table'); table.className = 'grs-clearance-table';
        const head = document.createElement('thead'); const heading = document.createElement('tr');
        for (const label of [labels.variant || 'Variant', labels.before || 'Original trade price', labels.now || 'Clearance price']) { const th=text('th',label); th.scope='col'; heading.append(th); }
        head.append(heading); table.append(head);
        const body = document.createElement('tbody');
        for (const row of rows) { const tr=document.createElement('tr'); const title=text('th',row.variant.title); title.scope='row'; const before=document.createElement('td'); before.append(text('s',money(row).format(row.before),'grs-clearance-before')); const now=document.createElement('td'); now.append(text('strong',money(row).format(row.now),'grs-clearance-now')); tr.append(title,before,now); body.append(tr); }
        table.append(body); output.append(table);
      }
      this.hidden = false;
    }
  }

  const beforeCache = new Map();
  class CartClearance extends HTMLElement {
    connectedCallback() {
      this.onRefresh = () => this.refresh();
      window.addEventListener('grs:clearance-cart-update', this.onRefresh);
      this.refresh();
    }
    disconnectedCallback() {
      window.removeEventListener('grs:clearance-cart-update', this.onRefresh);
      this.generation = (this.generation || 0) + 1;
    }
    async refresh() {
      const generation = this.generation = (this.generation || 0) + 1;
      this.replaceChildren();
      try {
        const data = this.parentNode?.dataset || {};
        if (!data.sku || !data.slug) return;
        const spark = await waitForSpark();
        if (!spark || !this.isConnected || generation !== this.generation) return;
        const slug = String(data.slug).split('/').filter(Boolean).pop();
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(root + 'products/' + encodeURIComponent(slug), location.origin);
        url.searchParams.set('view', 'grs-clearance-data');
        const key = url.href;
        if (!beforeCache.has(key)) beforeCache.set(key, fetch(url, {credentials:'same-origin',cache:'no-store'})
          .then(response => { if (!response.ok) throw new Error('Clearance data unavailable'); return response.json(); })
          .catch(error => { beforeCache.delete(key); throw error; }));
        const product = await beforeCache.get(key);
        if (product.clearance !== true) return;
        const variant = product.variants?.find(v => v.sku === data.sku);
        if (!variant || !(Number(variant.gbp) > 0 || Number(variant.eur) > 0)) return;
        const pricing = await spark.getPricingForVariant(String(product.id), data.sku);
        const row = eligible(variant, pricing);
        if (!row || !this.isConnected || generation !== this.generation) return;
        const line = document.createElement('div');
        line.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:6px;font-size:12px';
        const badge = text('span', window.grsClearanceLabels?.badge || 'Clearance');
        badge.style.cssText = 'background:#a62720;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700';
        const before = text('s', new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currency:row.currency}).format(row.before));
        before.setAttribute('aria-label', 'Original trade price per unit');
        line.append(badge, text('span', 'Original unit price:'), before);
        this.replaceChildren(line);
      } catch (error) {
        if (window.Shopify?.theme?.role !== 'main') console.debug('[GRS cart clearance]', error.message);
      }
    }
  }
  if (!customElements.get('grs-cart-clearance')) customElements.define('grs-cart-clearance', CartClearance);

  if (!customElements.get('grs-trade-clearance')) customElements.define('grs-trade-clearance', TradeClearance);
  window.addEventListener('grs:clearance-reset', () => {
    beforeCache.clear();
    document.querySelectorAll('grs-trade-clearance:not([data-demo])').forEach(el => { el.generation = (el.generation || 0) + 1; el.hidden = true; });
  });
})();
